/* eslint-disable import/prefer-default-export */
import config from 'config';
import { useQueryClient } from '@tanstack/react-query';
import qs from 'qs';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { useAuthenticatedApiClient } from 'utils/apiClient/base';
import { getPrefixUrl, ModuleNames } from 'utils/apiClient/url';
import {
  Account,
  GetPayoutsQueryParams,
  GetPayoutsResponse,
  GetTimelineTransactionsResponse,
  GetTransactionsQueryParams,
  GetTransactionsResponse,
  PayoutTransactions,
  Reader,
  TransactionDetails,
  TransactionRefund,
  GetInvoicesResponse,
  GetInvoicesQueryParams,
  ReportPayload,
  Settings,
  StripeLink,
  EmailReportPayload,
  ReportDownload,
  InvoiceUrl,
} from './types';

const { IN_PRODUCTION } = config;

const SHOREPAY_ACCESS_KEY_STAGING = '534bb0ee6d340c1c4da67d6e';
const SHOREPAY_ACCESS_KEY_PRODUCTION = '97b0154562a75e62fb3b2949';

const accessKey = IN_PRODUCTION
  ? SHOREPAY_ACCESS_KEY_PRODUCTION
  : SHOREPAY_ACCESS_KEY_STAGING;

export const useShorePayApi = () => {
  const privateApiClient = useAuthenticatedApiClient();
  const [account] = useLocalStorageState<AccountPersistInfo>('account');

  const shorePayApiClient = privateApiClient.extend({
    prefixUrl: getPrefixUrl(ModuleNames.SHORE_PAY),
    headers: {
      'shorepay-access-key': accessKey,
      Authorization: `Token ${account?.userToken}`,
    },
  });

  const queryClient = useQueryClient();
  return {
    fetchAccount: () => shorePayApiClient.get('account/').json<Account>(),
    createAccount: () =>
      shorePayApiClient
        .post(`account/`, {
          headers: {
            'Content-type': 'application/json',
          },
        })
        .json<Account>(),
    fetchTransactions: (params: GetTransactionsQueryParams = {}) =>
      shorePayApiClient
        .get('transactions/', {
          searchParams: qs.stringify(params),
        })
        .json<GetTransactionsResponse>(),

    fetchPayouts: (params: GetPayoutsQueryParams = {}) =>
      shorePayApiClient
        .get('payouts/', {
          searchParams: qs.stringify(params),
        })
        .json<GetPayoutsResponse>(),

    fetchPayoutTransactions: (id: string, params: Record<string, any>) =>
      shorePayApiClient
        .get(`payouts/${id}/transactions/`, {
          searchParams: qs.stringify(params),
        })
        .json<PayoutTransactions>(),

    fetchTimelineTransactions: (params: Record<string, any> = {}) =>
      shorePayApiClient
        .get('timeline/transactions/', {
          searchParams: qs.stringify(params),
        })
        .json<GetTimelineTransactionsResponse>(),

    fetchTransactionDetails: (id: string) =>
      shorePayApiClient.get(`transactions/${id}/`).json<TransactionDetails>(),

    createTransactionRefund: (id: string) =>
      shorePayApiClient
        .post(`transactions/${id}/refund/`)
        .json<TransactionRefund>(),

    fetchReaders: () =>
      Promise.resolve({
        data: [
          {
            id: 1,
            reader_id: 'id12usatoheusahoseuhasnoe',
            name: 'xyzasoehusaohesuahoes',
            status: 'Active',
            last_active: new Date().toISOString(),
          },
          {
            id: 2,
            reader_id: 'id1235',
            name: 'xyzj',
            status: 'Cancelled',
            last_active: new Date().toISOString(),
          },
          {
            id: 3,
            reader_id: 'id12344',
            name: 'xyzxp',
            status: 'Ordered',
            last_active: null,
          },
        ] as Reader[],
      }),

    fetchInvoices: (params: GetInvoicesQueryParams) =>
      shorePayApiClient
        .get(`invoices/`, {
          searchParams: qs.stringify(params),
        })
        .json<GetInvoicesResponse>(),

    fetchUrl: () => {
      const id: any = queryClient.getQueryData(['shorepay', 'active-invoice']);

      return shorePayApiClient.get(`invoices/${id}/url/`).json<InvoiceUrl>();
    },

    createTransactionReport: (payload: ReportPayload) =>
      shorePayApiClient
        .post('download/transaction/', {
          headers: {
            'Content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        })
        .json(),

    fetchTransactionReport: () => {
      const id: number | undefined = queryClient.getQueryData([
        'shorepay',
        'transaction-report-id',
      ]);
      return shorePayApiClient
        .get(`download/transaction/${id}/`)
        .json<ReportDownload>();
    },

    createPayoutReport: (payload: ReportPayload) =>
      shorePayApiClient
        .post('download/payout/', {
          headers: {
            'Content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        })
        .json(),

    fetchPayoutReport: () => {
      const id: number | undefined = queryClient.getQueryData([
        'shorepay',
        'payout-report-id',
      ]);
      return shorePayApiClient
        .get(`download/payout/${id}/`)
        .json<ReportDownload>();
    },

    postSettings: (payload: Settings) =>
      shorePayApiClient
        .post(`account/setting/`, {
          headers: {
            'Content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        })
        .json<Settings>(),
    fetchDashboardLink: () =>
      shorePayApiClient.get('account/link/').json<StripeLink>(),
    mailTransactionReport: (payload: EmailReportPayload) =>
      shorePayApiClient
        .post('email/transaction/', {
          headers: {
            'Content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        })
        .json(),
  };
};
